'use client';

import { type ReactNode, createContext, useRef, useContext } from 'react';
import { type StoreApi, useStore } from 'zustand';

interface UserStoreProviderProps {
  children: ReactNode;
}

export type ContextBoundedStoreProps<T> = {
  createFunction: () => StoreApi<T>;
};

export const createContextBoundedStore = <T,>(
  props: ContextBoundedStoreProps<T>
) => {
  const { createFunction } = props;

  const StoreContext = createContext<StoreApi<T> | null>(null);

  const StoreProvider = ({ children }: UserStoreProviderProps) => {
    const storeRef = useRef<StoreApi<T>>();
    if (!storeRef.current) {
      storeRef.current = createFunction();
    }

    return (
      <StoreContext.Provider value={storeRef.current}>
        {children}
      </StoreContext.Provider>
    );
  };

  const useStoreHook = <SELECTED,>(selector: (state: T) => SELECTED) => {
    const storeContext = useContext(StoreContext);

    if (!storeContext) {
      throw new Error(`useStore must be use within a store context`);
    }

    return useStore(storeContext, selector);
  };

  return { provider: StoreProvider, hook: useStoreHook };
};
